import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatbotAPI, extractAPI } from '../services/api';
import { X, Send, ArrowRight, FileText, Receipt, BookOpenCheck, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/* CSS-based purple robot mascot matching the reference image */
function RobotMascot({ size = 56, waving = true }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
      {/* Antennae */}
      <path d="M45 28 Q50 10 55 28" stroke="#1a1a1a" strokeWidth="3" fill="none" />
      <path d="M65 28 Q70 10 75 28" stroke="#1a1a1a" strokeWidth="3" fill="none" />
      {/* Head - purple helmet */}
      <rect x="28" y="24" width="64" height="46" rx="20" fill="#9400D3" />
      {/* Headphones */}
      <rect x="22" y="36" width="12" height="20" rx="6" fill="#9400D3" stroke="#1a1a1a" strokeWidth="2" />
      <rect x="86" y="36" width="12" height="20" rx="6" fill="#9400D3" stroke="#1a1a1a" strokeWidth="2" />
      {/* Face visor */}
      <rect x="36" y="32" width="48" height="32" rx="14" fill="#1a1a1a" />
      {/* Eyes */}
      <ellipse cx="48" cy="47" rx="6" ry="7" fill="white" />
      <ellipse cx="72" cy="47" rx="6" ry="7" fill="white" />
      {/* Smile */}
      <path d="M52 55 Q60 61 68 55" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Body */}
      <rect x="36" y="70" width="48" height="36" rx="14" fill="#9400D3" />
      {/* Chest badge */}
      <circle cx="60" cy="84" r="9" fill="#1a1a1a" />
      <path d="M56 82 L60 78 L64 82 L64 88 L56 88Z" fill="white" />
      {/* Left arm */}
      <g className={waving ? 'animate-wave origin-[38px_74px]' : ''}>
        <rect x="24" y="74" width="14" height="12" rx="6" fill="#1a1a1a" />
        <rect x="18" y="82" width="14" height="18" rx="7" fill="#9400D3" />
      </g>
      {/* Right arm - waving */}
      <g className={waving ? 'animate-wave-right origin-[82px_74px]' : ''}>
        <rect x="82" y="74" width="14" height="12" rx="6" fill="#1a1a1a" />
        <rect x="88" y="82" width="14" height="18" rx="7" fill="#9400D3" />
      </g>
      {/* Legs */}
      <rect x="42" y="104" width="14" height="14" rx="7" fill="#9400D3" />
      <rect x="64" y="104" width="14" height="14" rx="7" fill="#9400D3" />
    </svg>
  );
}

export default function Chatbot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hi! I\'m the Transsolution assistant. I can help you:\n- Navigate the app\n- Create receipts/invoices from uploaded PDFs\n- Answer questions about accounting features\n\nTry uploading a PDF or ask me anything!' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(null);
  const [position, setPosition] = useState(() => {
    try { const s = localStorage.getItem('chatbot_pos'); return s ? JSON.parse(s) : { x: window.innerWidth - 100, y: 80 }; } catch { return { x: 1800, y: 80 }; }
  });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragOffset.current = { x: clientX - position.x, y: clientY - (window.innerHeight - position.y - 56) };
    setDragging(true);
  }, [position]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const newX = Math.max(0, Math.min(window.innerWidth - 60, clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 60, window.innerHeight - (clientY - dragOffset.current.y) - 56));
      setPosition({ x: newX, y: newY });
    };
    const onEnd = () => {
      setDragging(false);
      localStorage.setItem('chatbot_pos', JSON.stringify(position));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onEnd);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onEnd); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
  }, [dragging, position]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const res = await chatbotAPI.send(msg);
      const reply = res.data.reply;
      const navMatch = reply.match(/\(\/[a-z-/]+\)/g);
      setMessages(m => [...m, { role: 'bot', text: reply, navLinks: navMatch ? navMatch.map(p => p.slice(1,-1)) : [] }]);
    } catch {
      setMessages(m => [...m, { role: 'bot', text: 'Sorry, I couldn\'t process that. Please try again.' }]);
    }
    finally { setLoading(false); }
  };

  const startUpload = (type) => { setUploadMode(type); fileRef.current?.click(); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadMode) return;
    const typeLabel = uploadMode === 'receipt' ? 'Loading Receipt' : uploadMode === 'invoice' ? 'Tax Invoice' : 'Ledger';
    setMessages(m => [...m, { role: 'user', text: `Uploading ${file.name} to create ${typeLabel}...` }]);
    setLoading(true);
    try {
      if (uploadMode === 'ledger') {
        setMessages(m => [...m, { role: 'bot', text: `To import ledger entries from "${file.name}", please go to the Ledger page and use the import feature there.`, navLinks: ['/ledger'] }]);
      } else {
        const res = await extractAPI.pdf(uploadMode, file);
        const d = res.data.extracted;
        if (d) {
          const fields = Object.entries(d).filter(([k,v]) => v && typeof v === 'string').slice(0, 6).map(([k,v]) => `${k}: ${v}`).join('\n');
          const path = uploadMode === 'receipt' ? '/receipts/new' : '/invoices/new';
          sessionStorage.setItem('prefill_data', JSON.stringify({ type: uploadMode, data: d }));
          setMessages(m => [...m, { role: 'bot', text: `Extracted from "${file.name}":\n${fields}\n\nClick below to create the ${typeLabel} with this data pre-filled.`, navLinks: [path], prefillAction: true }]);
        } else {
          setMessages(m => [...m, { role: 'bot', text: 'Could not extract data from this file. Please try a clearer PDF.' }]);
        }
      }
    } catch { setMessages(m => [...m, { role: 'bot', text: 'Failed to process the file.' }]); }
    finally { setLoading(false); setUploadMode(null); e.target.value = ''; }
  };

  if (!user) return null;

  if (!open) {
    return (
      <div style={{ position: 'fixed', left: position.x, bottom: position.y, zIndex: 60 }}
        className="flex items-center gap-1" data-testid="chatbot-toggle-wrap">
        <button
          onMouseDown={onDragStart} onTouchStart={onDragStart}
          className="w-6 h-6 rounded-full bg-purple-600/40 text-white flex items-center justify-center cursor-grab active:cursor-grabbing backdrop-blur-sm"
          data-testid="chatbot-drag-handle" title="Drag to move">
          <GripVertical className="w-3 h-3" />
        </button>
        <button onClick={() => setOpen(true)} data-testid="chatbot-toggle"
          className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 shadow-lg shadow-purple-300/40 dark:shadow-purple-900/40 flex items-center justify-center transition-all hover:scale-110 hover:shadow-xl hover:shadow-purple-400/50 p-1 overflow-hidden">
          <RobotMascot size={52} waving={true} />
        </button>
      </div>
    );
  }

  const getPanelStyle = () => {
    const panelW = 384; const panelH = 520;
    let left = position.x; let bottom = position.y;
    if (left + panelW > window.innerWidth) left = window.innerWidth - panelW - 16;
    if (left < 8) left = 8;
    if (bottom + panelH > window.innerHeight - 16) bottom = window.innerHeight - panelH - 16;
    if (bottom < 8) bottom = 8;
    return { position: 'fixed', left, bottom, zIndex: 60 };
  };

  return (
    <div style={getPanelStyle()}
      className="w-96 h-[520px] glass-card rounded-2xl shadow-2xl shadow-purple-200/30 dark:shadow-purple-900/30 flex flex-col overflow-hidden border-purple-200 dark:border-purple-800" data-testid="chatbot-panel">
      <div className="bg-gradient-to-r from-purple-700 to-purple-500 text-white px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <RobotMascot size={32} waving={false} />
          <div>
            <h3 className="font-heading font-bold text-sm">Transsolution AI</h3>
            <p className="text-[10px] text-purple-200">Ask anything about your accounts</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition" data-testid="chatbot-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-white/80 dark:bg-[hsl(260,15%,8%)]" data-testid="chatbot-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
              m.role === 'user' ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white' : 'bg-purple-50 dark:bg-purple-900/20 text-gray-800 dark:text-gray-200'
            }`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.navLinks?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.navLinks.map((link, j) => (
                    <button key={j} onClick={() => { navigate(link); setOpen(false); }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white/80 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-50">
                      <ArrowRight className="w-3 h-3" /> {link}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl px-3 py-2">
              <div className="flex gap-1"><span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" /><span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay:'0.15s'}} /><span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay:'0.3s'}} /></div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-purple-100 dark:border-purple-800 px-3 py-2 flex gap-1.5 shrink-0 bg-white/60 dark:bg-[hsl(260,15%,10%)]">
        <button onClick={() => startUpload('receipt')} disabled={loading}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50 dark:bg-purple-900/20 dark:text-purple-300" data-testid="chatbot-upload-receipt">
          <Receipt className="w-3.5 h-3.5" /> Upload LR
        </button>
        <button onClick={() => startUpload('invoice')} disabled={loading}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50 dark:bg-purple-900/20 dark:text-purple-300" data-testid="chatbot-upload-invoice">
          <FileText className="w-3.5 h-3.5" /> Upload Invoice
        </button>
        <button onClick={() => startUpload('ledger')} disabled={loading}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50 dark:bg-purple-900/20 dark:text-purple-300" data-testid="chatbot-upload-ledger">
          <BookOpenCheck className="w-3.5 h-3.5" /> Upload Ledger
        </button>
      </div>

      <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.csv" onChange={handleFile} className="hidden" />

      <div className="border-t border-purple-100 dark:border-purple-800 p-3 shrink-0 bg-white/60 dark:bg-[hsl(260,15%,10%)]">
        <div className="flex gap-2">
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Ask me anything..."
            className="form-input flex-1 text-sm" data-testid="chatbot-input" disabled={loading} />
          <button onClick={send} disabled={loading || !input.trim()} data-testid="chatbot-send"
            className="px-3 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 disabled:opacity-50 transition">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
